import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  parseISO,
  eachHourOfInterval,
  startOfDay,
  endOfDay,
  getHours,
  getMinutes
} from 'date-fns'
import type { CalendarEvent, EventType } from '@/api/communicationApi'

interface CalendarViewProps {
  events: CalendarEvent[]
  onDateClick?: (date: Date) => void
  onEventClick?: (event: CalendarEvent) => void
  onCreateEvent?: (date: Date) => void
  selectedDate?: Date
  isLoading?: boolean
}

const eventTypeColors: Record<EventType, { bg: string; border: string; text: string; dot: string }> = {
  academic: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', dot: 'bg-blue-500' },
  sports: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700', dot: 'bg-green-500' },
  cultural: { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700', dot: 'bg-purple-500' },
  holiday: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700', dot: 'bg-red-500' },
  exam: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700', dot: 'bg-orange-500' },
  meeting: { bg: 'bg-indigo-100', border: 'border-indigo-300', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  parent_teacher: { bg: 'bg-teal-100', border: 'border-teal-300', text: 'text-teal-700', dot: 'bg-teal-500' },
  other: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700', dot: 'bg-gray-500' }
}

export function CalendarView({
  events,
  onDateClick,
  onEventClick,
  onCreateEvent,
  selectedDate,
  isLoading = false
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState<'month' | 'week'>('month')

  // Calculate calendar days for month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentDate])

  // Calculate week days for week view
  const weekDaysForView = useMemo(() => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 })
    return eachDayOfInterval({ start: weekStart, end: weekEnd })
  }, [currentDate])

  // Group events by date
  const eventsByDate = useMemo(() => {
    const grouped: Record<string, CalendarEvent[]> = {}

    events.forEach(event => {
      const eventDate = format(parseISO(event.startDate), 'yyyy-MM-dd')
      if (!grouped[eventDate]) {
        grouped[eventDate] = []
      }
      grouped[eventDate].push(event)
    })

    return grouped
  }, [events])

  const handlePrev = () => {
    if (view === 'month') {
      setCurrentDate(subMonths(currentDate, 1))
    } else {
      setCurrentDate(subWeeks(currentDate, 1))
    }
  }
  const handleNext = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, 1))
    } else {
      setCurrentDate(addWeeks(currentDate, 1))
    }
  }
  const handleToday = () => setCurrentDate(new Date())

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const dateKey = format(date, 'yyyy-MM-dd')
    return eventsByDate[dateKey] || []
  }

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {view === 'month'
              ? format(currentDate, 'MMMM yyyy')
              : `${format(weekDaysForView[0], 'MMM d')} - ${format(weekDaysForView[6], 'MMM d, yyyy')}`
            }
          </h2>
          <div className="flex items-center space-x-1">
            <button
              onClick={handlePrev}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={view === 'month' ? 'Previous month' : 'Previous week'}
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={handleNext}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={view === 'month' ? 'Next month' : 'Next week'}
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>
          <button
            onClick={handleToday}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>

        <div className="flex items-center space-x-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                view === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                view === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Week
            </button>
          </div>

          {onCreateEvent && (
            <button
              onClick={() => onCreateEvent(selectedDate || new Date())}
              className="flex items-center space-x-1 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add Event</span>
            </button>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
              <p className="mt-2 text-sm text-gray-500">Loading events...</p>
            </div>
          </div>
        ) : view === 'month' ? (
          <>
            {/* Week day headers */}
            <div className="grid grid-cols-7 mb-2">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                const dayEvents = getEventsForDate(day)
                const isCurrentMonth = isSameMonth(day, currentDate)
                const isSelected = selectedDate && isSameDay(day, selectedDate)
                const dayIsToday = isToday(day)

                return (
                  <motion.div
                    key={day.toISOString()}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.01 }}
                    onClick={() => onDateClick?.(day)}
                    className={`min-h-24 p-1 border rounded-lg cursor-pointer transition-all ${
                      isCurrentMonth
                        ? isSelected
                          ? 'bg-primary-50 border-primary-300'
                          : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    {/* Day number */}
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${
                          dayIsToday
                            ? 'bg-primary-600 text-white'
                            : isCurrentMonth
                            ? 'text-gray-900'
                            : 'text-gray-400'
                        }`}
                      >
                        {format(day, 'd')}
                      </span>
                      {dayEvents.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {dayEvents.length} event{dayEvents.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Events */}
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((event) => {
                        const colors = eventTypeColors[event.eventType] || eventTypeColors.other
                        return (
                          <button
                            key={event.id}
                            onClick={(e) => {
                              e.stopPropagation()
                              onEventClick?.(event)
                            }}
                            className={`w-full text-left px-1.5 py-0.5 text-xs font-medium rounded truncate ${colors.bg} ${colors.text} hover:opacity-80 transition-opacity`}
                          >
                            {event.isAllDay ? (
                              event.title
                            ) : (
                              <>
                                <span className="text-gray-500">
                                  {format(parseISO(event.startDate), 'HH:mm')}
                                </span>{' '}
                                {event.title}
                              </>
                            )}
                          </button>
                        )
                      })}
                      {dayEvents.length > 3 && (
                        <p className="text-xs text-gray-500 px-1">
                          +{dayEvents.length - 3} more
                        </p>
                      )}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </>
        ) : (
          /* Week View */
          <>
            {/* Week day headers with dates */}
            <div className="grid grid-cols-8 border-b border-gray-200">
              <div className="p-2 text-xs font-medium text-gray-500 text-center border-r border-gray-200">
                Time
              </div>
              {weekDaysForView.map((day) => {
                const dayIsToday = isToday(day)
                return (
                  <div
                    key={day.toISOString()}
                    className={`p-2 text-center border-r border-gray-200 last:border-r-0 ${
                      dayIsToday ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="text-xs font-medium text-gray-500">{format(day, 'EEE')}</div>
                    <div className={`text-lg font-semibold ${dayIsToday ? 'text-primary-600' : 'text-gray-900'}`}>
                      {format(day, 'd')}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Week grid with hours */}
            <div className="max-h-[500px] overflow-y-auto">
              {Array.from({ length: 24 }, (_, hour) => (
                <div key={hour} className="grid grid-cols-8 border-b border-gray-100">
                  <div className="p-1 text-xs text-gray-500 text-right pr-2 border-r border-gray-200">
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  {weekDaysForView.map((day) => {
                    const dayEvents = getEventsForDate(day).filter(event => {
                      if (event.isAllDay) return hour === 0
                      const eventHour = getHours(parseISO(event.startDate))
                      return eventHour === hour
                    })
                    const dayIsToday = isToday(day)

                    return (
                      <div
                        key={day.toISOString()}
                        onClick={() => onDateClick?.(day)}
                        className={`min-h-[40px] p-0.5 border-r border-gray-100 last:border-r-0 cursor-pointer hover:bg-gray-50 ${
                          dayIsToday ? 'bg-primary-50/30' : ''
                        }`}
                      >
                        {dayEvents.map((event) => {
                          const colors = eventTypeColors[event.eventType] || eventTypeColors.other
                          return (
                            <button
                              key={event.id}
                              onClick={(e) => {
                                e.stopPropagation()
                                onEventClick?.(event)
                              }}
                              className={`w-full text-left px-1 py-0.5 text-xs font-medium rounded truncate ${colors.bg} ${colors.text} hover:opacity-80`}
                            >
                              {event.isAllDay ? (
                                <span className="text-xs">All day: {event.title}</span>
                              ) : (
                                <>
                                  {format(parseISO(event.startDate), 'HH:mm')} {event.title}
                                </>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Legend */}
      <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-xs font-medium text-gray-500">Event Types:</span>
          {Object.entries(eventTypeColors).map(([type, colors]) => (
            <div key={type} className="flex items-center space-x-1.5">
              <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
              <span className="text-xs text-gray-600 capitalize">
                {type.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default CalendarView
